/** A2A → Provenance: the DAG audit trail, chain verification, signed checkpoints. */
import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '../../components/ui';
import ProvenanceDag from '../../components/a2a/ProvenanceDag';
import { a2a, A2aEvent, ProvNode, statusBadge } from '../../lib/a2aApi';
import { usePermission } from '../../hooks/usePermission';
import '../../components/a2a/a2a.css';
import { useTranslation } from 'react-i18next';
import { parseServerDate } from '../../i18n/format';

const A2AProvenance: React.FC<{ apiUrl: string }> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const canWrite = usePermission('update:policy');
  const [events, setEvents] = useState<A2aEvent[]>([]); const [chain, setChain] = useState<any>(null);
  const [prov, setProv] = useState<{ root: string; nodes: ProvNode[]; intact: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null); const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { const [e, v] = await Promise.all([a2a.events(apiUrl, { limit: 300 }), a2a.verify(apiUrl)]); setEvents(e.events); setChain(v); setError(null); }
    catch (e: any) { setError(e.message); }
  }, [apiUrl]);
  useEffect(() => { void load(); }, [load]);

  const cp = async () => { try { const r = await a2a.checkpoint(apiUrl); setNote(`Signed checkpoint over ${r.rowCount} events (key ${r.kid}).`); await load(); } catch (e: any) { setError(e.message); } };

  return (
    <div className="page-content">
      <div className="page-title"><div><h1>{t('pages.a2a.a2AProvenance.provenance')}</h1><p className="subtitle">{t('pages.a2a.a2AProvenance.everyDelegationReadReviewAndDenial')}</p></div>
        <div className="a2a-row"><button className="ent-btn" onClick={load}><Icon name="refresh" /> {t('pages.a2a.a2AProvenance.reVerifyChain')}</button>{canWrite && <button className="ent-btn" onClick={cp}><Icon name="lock" /> {t('pages.a2a.a2AProvenance.signCheckpoint')}</button>}</div></div>
      {error && <div className="notice error"><Icon name="warning" /> {error}</div>}
      {note && <div className="notice success"><Icon name="checkCircle" /> {note}</div>}
      {chain && <div className={`a2a-banner ${chain.ok ? 'ok' : 'err'}`}>
        {chain.ok ? `Chain intact — ${chain.rowsChecked} events verified.` : `Chain BROKEN: ${chain.detail}`}
        {chain.checkpoint && <>{t('pages.a2a.a2AProvenance.checkpointCovers', { count: chain.checkpoint.coveredRows })} {chain.checkpoint.signatureValid ? t('pages.a2a.a2AProvenance.signatureValid') : t('pages.a2a.a2AProvenance.signatureMismatch')}.</>}
        {!chain.checkpoint && <> {t('pages.a2a.a2AProvenance.noSignedCheckpointYetTheChain')}</>}
      </div>}
      <div style={{ display: 'grid', gap: 14 }}>
        <div className="ent-card"><div className="ent-card-head">{t('pages.a2a.a2AProvenance.eventsClickOneToWalkIts')}</div><div className="ent-card-body" style={{ maxHeight: 280, overflow: 'auto' }}>
          <table className="ent-table"><thead><tr><th>{t('pages.a2a.a2AProvenance.event')}</th><th>{t('pages.a2a.a2AProvenance.agent')}</th><th>{t('pages.a2a.a2AProvenance.hop')}</th><th>{t('pages.a2a.a2AProvenance.time')}</th></tr></thead><tbody>
            {events.map((e) => <tr key={e.event_hash} style={{ cursor: 'pointer' }} onClick={async () => setProv(await a2a.provenance(apiUrl, e.event_hash))}>
              <td><span className={`badge ${statusBadge(e.status)}`}>{e.event_kind}</span></td><td>{e.agent_id || e.principal || '—'}</td><td>{e.hop ?? '—'}</td><td className="a2a-muted">{parseServerDate(e.ts).toLocaleTimeString()}</td></tr>)}
          </tbody></table></div></div>
        <div>{prov ? <ProvenanceDag root={prov.root} nodes={prov.nodes} intact={prov.intact} /> : <div className="ent-empty">{t('pages.a2a.a2AProvenance.selectAnEventToWalkIts')}</div>}</div>
      </div>
    </div>
  );
};
export default A2AProvenance;
